// @ts-nocheck

import React, { useMemo, useState } from 'react';

import { FormDialog } from '@components/admin/FormDialog';
import { FormTitle } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import {
  DomesticTrackingDetailsForm,
  TrackingDetailsForm,
} from '@components/admin/widgets/CargoTracking/CargoTrackingForms';
import { cargoTrackingStrings } from '@data/cargoTracking';
import { useTrackingData } from '@hooks/queries/dashboard/useTrackingData';
import { useSearch } from '@hooks/useSearch';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { getBreadcrumb, url } from '@utils/pages';
import { toCSTDate, toCSTTime, toJSTDate, toJSTTime } from '@utils/types';
import clsx from 'clsx';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '輸送詳細',
    index: 'NO.',
    d2dNo: 'D2D番号',
    edit: '編集',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
    '& a:hover': {
      color: theme.palette.primary.light,
    },
    '& a': {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  trackingTable: {
    ...common.middleText,
    borderSpacing: 0,
    '& th': {
      ...common.fontWeight500,
      ...common.textGray800,
      backgroundColor: theme.palette.background.default,
      textAlign: 'left',
      padding: 20,
    },
    '& td': {
      ...common.textGray700,
      textAlign: 'left',
      padding: '7px 20px',
    },
  },
}));

const searchType = t.type({
  d2dNo: h.string(),
});

export const CargoTrackingDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search } = useSearch(searchType);
  const trackingData = useTrackingData(search.d2dNo);
  const [currentForm, setCurrentForm] = useState<React.ReactElement | null>(null);
  const [currentTitle, setCurrentTitle] = useState('');

  const getJSTDateString = (item: { time: string } | null | undefined) => {
    return item?.time ? toJSTDate(new Date(item?.time)) : '-';
  };

  const getCSTDateString = (item: { time: string } | null | undefined) => {
    return item?.time ? toCSTDate(new Date(item?.time)) : '-';
  };

  const getJSTTimeString = (item: { time: string } | null | undefined) => {
    return item?.time ? toJSTTime(new Date(item?.time)) : '-';
  };

  const getCSTTimeString = (item: { time: string } | null | undefined) => {
    return item?.time ? toCSTTime(new Date(item?.time)) : '-';
  };

  const getFileLink = (
    item: { file: string | undefined | null } | null | undefined,
    fileName: string,
  ) => {
    return item?.file ? (
      <a href={item.file} target="_blank" rel="noreferrer">
        {fileName}
      </a>
    ) : (
      '-'
    );
  };

  const breadcrumb = useMemo(
    () => [...getBreadcrumb(url('/admin/widgets/CargoTracking')), search.d2dNo],
    [],
  );

  return (
    <Layout title={strings.title} loading={trackingData.isLoading} breadcrumb={breadcrumb}>
      {() => {
        const trackingDataDict = trackingData.data!.data!;

        return (
          <>
            <FormDialog
              open={currentForm !== null}
              title={currentTitle}
              onClose={() => setCurrentForm(null)}
            >
              {currentForm}
            </FormDialog>

            <div className={classes.container}>
              <FormTitle title={strings.title} noEdit />

              <table className={clsx(classes.trackingTable)}>
                <tbody>
                  <tr>
                    <th>{cargoTrackingStrings.transportStatus}</th>

                    <th>{cargoTrackingStrings.date}</th>

                    <th>{cargoTrackingStrings.time}</th>

                    <th>{cargoTrackingStrings.place}</th>

                    <th>{cargoTrackingStrings.files}</th>
                  </tr>

                  <tr>
                    <td>{cargoTrackingStrings.done}</td>

                    <td>
                      {getJSTDateString(trackingDataDict[cargoTrackingStrings.domesticDelivered])}
                    </td>

                    <td>
                      {getJSTTimeString(trackingDataDict[cargoTrackingStrings.domesticDelivered])}
                    </td>

                    <td>-</td>

                    <td>
                      -
                      <a
                        style={{ float: 'right' }}
                        onClick={() => {
                          setCurrentTitle(cargoTrackingStrings.domesticDelivered);
                          setCurrentForm(
                            <DomesticTrackingDetailsForm
                              trackNo={search.d2dNo}
                              transportStatus={cargoTrackingStrings.domesticDelivered}
                              item={trackingDataDict[cargoTrackingStrings.domesticDelivered]}
                            />,
                          );
                        }}
                      >
                        {strings.edit}
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td>{cargoTrackingStrings.domesticShipped}</td>

                    <td>
                      {getJSTDateString(trackingDataDict[cargoTrackingStrings.domesticShipped])}
                    </td>

                    <td>
                      {getJSTTimeString(trackingDataDict[cargoTrackingStrings.domesticShipped])}
                    </td>

                    <td>-</td>

                    <td>
                      -
                      <a
                        style={{ float: 'right' }}
                        onClick={() => {
                          setCurrentTitle(cargoTrackingStrings.domesticShipped);
                          setCurrentForm(
                            <DomesticTrackingDetailsForm
                              trackNo={search.d2dNo}
                              transportStatus={cargoTrackingStrings.domesticShipped}
                              item={trackingDataDict[cargoTrackingStrings.domesticShipped]}
                            />,
                          );
                        }}
                      >
                        {strings.edit}
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td>{cargoTrackingStrings.importDeclared}</td>

                    <td>
                      {getJSTDateString(trackingDataDict[cargoTrackingStrings.importDeclared])}
                    </td>

                    <td>
                      {getJSTTimeString(trackingDataDict[cargoTrackingStrings.importDeclared])}
                    </td>

                    <td>{trackingDataDict[cargoTrackingStrings.importDeclared]?.place}</td>

                    <td>
                      {getFileLink(
                        trackingDataDict[cargoTrackingStrings.importDeclared],
                        '輸入許可書',
                      )}

                      <a
                        style={{ float: 'right' }}
                        onClick={() => {
                          setCurrentTitle(cargoTrackingStrings.importDeclared);
                          setCurrentForm(
                            <TrackingDetailsForm
                              trackNo={search.d2dNo}
                              transportStatus={cargoTrackingStrings.importDeclared}
                              item={trackingDataDict[cargoTrackingStrings.importDeclared]}
                            />,
                          );
                        }}
                      >
                        {strings.edit}
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td>{cargoTrackingStrings.importArrived}</td>

                    <td>
                      {getJSTDateString(trackingDataDict[cargoTrackingStrings.importArrived])}
                    </td>

                    <td>
                      {getJSTTimeString(trackingDataDict[cargoTrackingStrings.importArrived])}
                    </td>

                    <td>{trackingDataDict[cargoTrackingStrings.importArrived]?.place}</td>

                    <td>
                      -
                      <a
                        style={{ float: 'right' }}
                        onClick={() => {
                          setCurrentTitle(cargoTrackingStrings.importArrived);
                          setCurrentForm(
                            <TrackingDetailsForm
                              trackNo={search.d2dNo}
                              transportStatus={cargoTrackingStrings.importArrived}
                              item={trackingDataDict[cargoTrackingStrings.importArrived]}
                              noFile
                            />,
                          );
                        }}
                      >
                        {strings.edit}
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td>{cargoTrackingStrings.exportMovedOut}</td>

                    <td>
                      {getCSTDateString(trackingDataDict[cargoTrackingStrings.exportMovedOut])}
                    </td>

                    <td>
                      {getCSTTimeString(trackingDataDict[cargoTrackingStrings.exportMovedOut])}
                    </td>

                    <td>{trackingDataDict[cargoTrackingStrings.exportMovedOut]?.place}</td>

                    <td>
                      {getFileLink(
                        trackingDataDict[cargoTrackingStrings.exportMovedOut],
                        'BL送り状',
                      )}

                      <a
                        style={{ float: 'right' }}
                        onClick={() => {
                          setCurrentTitle(cargoTrackingStrings.exportMovedOut);
                          setCurrentForm(
                            <TrackingDetailsForm
                              trackNo={search.d2dNo}
                              transportStatus={cargoTrackingStrings.exportMovedOut}
                              item={trackingDataDict[cargoTrackingStrings.exportMovedOut]}
                            />,
                          );
                        }}
                      >
                        {strings.edit}
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td>{cargoTrackingStrings.exportMovedIn}</td>

                    <td>
                      {getCSTDateString(trackingDataDict[cargoTrackingStrings.exportMovedIn])}
                    </td>

                    <td>
                      {getCSTTimeString(trackingDataDict[cargoTrackingStrings.exportMovedIn])}
                    </td>

                    <td>{trackingDataDict[cargoTrackingStrings.exportMovedIn]?.place}</td>

                    <td>
                      {getFileLink(trackingDataDict[cargoTrackingStrings.exportMovedIn], '進倉単')}

                      <a
                        style={{ float: 'right' }}
                        onClick={() => {
                          setCurrentTitle(cargoTrackingStrings.exportMovedIn);
                          setCurrentForm(
                            <TrackingDetailsForm
                              trackNo={search.d2dNo}
                              transportStatus={cargoTrackingStrings.exportMovedIn}
                              item={trackingDataDict[cargoTrackingStrings.exportMovedIn]}
                            />,
                          );
                        }}
                      >
                        {strings.edit}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        );
      }}
    </Layout>
  );
};
